import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const TranslationsContext = createContext(null);

export const useTranslations = () => {
  const context = useContext(TranslationsContext);
  if (!context) {
    throw new Error('useTranslations must be used within TranslationsProvider');
  }
  return context;
};

export const TranslationsProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const currentLang = i18n.language?.split('-')[0] || 'pl';

  // Fetch all translations from database
  const fetchTranslations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/translations`);
      
      // Transform array to object keyed by translation key
      const translationsMap = {};
      response.data.forEach(item => {
        if (item.translations) {
          // New format: { key, translations: { pl, en, ... } }
          translationsMap[item.key] = item.translations;
        } else if (item.key && item.value && item.lang) {
          // Old format: { key, value, lang }
          if (!translationsMap[item.key]) {
            translationsMap[item.key] = {};
          }
          translationsMap[item.key][item.lang] = item.value;
        }
      });
      
      setTranslations(translationsMap);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch translations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTranslations();
  }, [fetchTranslations]);

  // Get translation for a key in current language with fallback
  const td = useCallback((key, fallback = null) => {
    const translation = translations[key];
    if (!translation) {
      return fallback || key;
    }
    
    // Try current language
    if (translation[currentLang]) {
      return translation[currentLang];
    }
    
    // Fallback to Polish
    if (translation.pl) {
      return translation.pl;
    }
    
    // Fallback to English
    if (translation.en) {
      return translation.en;
    }
    
    // Return any available translation
    const availableLangs = Object.keys(translation);
    if (availableLangs.length > 0) {
      return translation[availableLangs[0]];
    }
    
    return fallback || key;
  }, [translations, currentLang]);

  // Get all translations for a key (all languages)
  const getAllTranslations = useCallback((key) => {
    return translations[key] || {};
  }, [translations]);

  // Check if a key exists
  const hasTranslation = useCallback((key) => {
    return !!translations[key];
  }, [translations]);

  // Refresh translations
  const refresh = useCallback(() => {
    return fetchTranslations();
  }, [fetchTranslations]);

  // Smart translate: database first, fallback to i18n
  // This allows admin panel to override any translation
  const tr = useCallback((key, i18nFallback = null) => {
    const dbValue = td(key);
    // If database has a value (not just returning the key back)
    if (dbValue !== key && dbValue !== null && dbValue !== undefined) {
      return dbValue;
    }
    // Return i18n fallback or the key itself
    return i18nFallback !== null ? i18nFallback : key;
  }, [td]);

  const value = {
    translations,
    loading,
    error,
    td,  // "translate from database" 
    tr,  // "translate" with i18n fallback support
    getAllTranslations,
    hasTranslation,
    refresh,
    currentLang
  };

  return (
    <TranslationsContext.Provider value={value}>
      {children}
    </TranslationsContext.Provider>
  );
};

export default TranslationsContext;
